import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Card, Tag, Spin, Tooltip, ButtonGroup, Button, Empty, Banner, Toast,
} from "@douyinfe/semi-ui";
import { VChart } from "@visactor/react-vchart";
import {
  IconHistogram, IconPhoneStroke, IconCalendar,
  IconStar, IconBranch, IconChevronDown, IconChevronRight, IconAlertTriangle,
  IconList, IconTick, IconArrowLeft,
} from "@douyinfe/semi-icons";
import { ApiClient } from "../../api/ApiClient";
import dayjs from "dayjs";

const apiClient = ApiClient.getDefault();

// ============ 类型 ============
interface PortraitData {
  identity: {
    userName: string;
    teamName: string;
    roleName: string;
    entryDays: number | null;
    online: boolean | null;
  };
  coreMetrics: {
    todayAssignedNew: number;
    pendingCount: number;
    overdueCount: number;
    todayFollowNew: number;
    todayFollowOld: number;
    todayVisit: number;
    monthVisit: number;
    monthAssignedNew: number;
    oneHourFollowRate: number;
  };
  customerStructure: {
    totalCustomers: number;
    starDistribution: Record<string, number>;
    statusDistribution: Record<string, number>;
    tagDistribution: Record<string, number>;
    newCount: number;
    favCount: number;
    reassignedCount: number;
    monthChurn: number;
  };
  followBehavior: {
    oneHourFollowRate: number;
    avgSignedFollowCount: number;
    avgChurnFollowCount: number;
    weakFollowNewCount: number;
    dailyHeatmap: Record<string, number>;
    hourlyDistribution: Record<number, number>;
    dailyAvgFollowCount: number;
  };
  funnel: {
    totalAssigned: number;
    effectiveFirstFollow: number;
    hasIntent: number;
    visited: number;
    signed: number;
    intentToVisitRate: number;
  };
}

// ============ 设计 Token（统一颜色 / 字号 / 圆角 / 间距） ============
const T = {
  color: {
    primary: "#1e2b5a",
    primarySoft: "#4f6faa",
    text: "#0f172a",
    textSub: "#475569",
    textMuted: "#94a3b8",
    bg: "#f4f6fb",
    cardBg: "#ffffff",
    itemBg: "#f9fafc",
    border: "#e2e8f0",
    success: "#16a34a",
    successBg: "#e4f4eb",
    warning: "#f59e0b",
    warningBg: "#fff7e6",
    danger: "#dc2626",
    dangerBg: "#fef2f2",
  },
  fontSize: { xs: 12, sm: 13, md: 15, lg: 20, xl: 28 },
  radius: { sm: 8, md: 12, lg: 20 },
  space: { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24 },
} as const;

// 客户状态分桶（让饼图分类更聚焦）
const STATUS_GROUPS = [
  { keys: ["PENDING_CONTACT"], label: "未处理", color: "#94a3b8" },
  { keys: ["UNREACHABLE"], label: "待跟进", color: "#8a9bc7" },
  { keys: ["INVITATION_IN_PROGRESS", "PENDING_VISIT"], label: "意向类", color: "#f59e0b" },
  { keys: ["VISITED", "SIGNED_CONTRACT", "PAID"], label: "成交类", color: "#16a34a" },
  { keys: ["OUT_LEAD", "NON_COMPLIANCE", "LOST"], label: "流失类", color: "#dc2626" },
];

const STAR_COLORS = ["#b9c8e0", "#8a9bc7", "#5c72a8", "#2d4b8e", "#1e2b5a"];
const TAG_LABELS: Record<string, string> = {
  hasHouse: "有房", hasVehicle: "有车", hasCredit: "征信", hasProvidentFund: "公积金",
};

const FUNNEL_STEPS = [
  { key: "totalAssigned", label: "分配新客" },
  { key: "effectiveFirstFollow", label: "有效首跟" },
  { key: "hasIntent", label: "产生意向" },
  { key: "visited", label: "邀约上门" },
  { key: "signed", label: "签约放款" },
] as const;

const PERIOD_LABEL: Record<string, string> = { today: "今日", week: "本周", month: "本月" };
const fontFamily = "'Inter', 'PingFang SC', 'Microsoft YaHei', system-ui, -apple-system, sans-serif";

// ============ 公共样式 ============
const cardStyle: React.CSSProperties = {
  background: T.color.cardBg, borderRadius: T.radius.lg, padding: T.space[5],
  boxShadow: "0 8px 20px rgba(0,0,0,0.04), 0 2px 6px rgba(0,20,40,0.04)",
  border: "none", fontFamily,
};

const metricItemStyle: React.CSSProperties = {
  background: T.color.itemBg, borderRadius: T.radius.md, padding: T.space[3],
  border: `1px solid ${T.color.border}`, display: "flex", flexDirection: "column",
  gap: T.space[1], minWidth: 0,
};

const sectionTitleStyle: React.CSSProperties = {
  fontWeight: 600, fontSize: T.fontSize.md, color: T.color.text,
  display: "flex", alignItems: "center", gap: T.space[2], margin: 0, fontFamily,
};

const infoIconStyle: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 14, height: 14, color: T.color.textMuted, cursor: "help", marginLeft: 4,
};

// ============ 工具函数 ============
function pct(value: number | undefined | null, digits = 0): string {
  if (value == null || Number.isNaN(value)) return "0";
  return value.toFixed(digits);
}

/** 生成当月所有天，标注未来日期，用于热力图 */
function generateMonthDays(heatmap: Record<string, number>) {
  const now = dayjs();
  const daysInMonth = now.daysInMonth();
  const today = now.date();
  const list: { date: string; count: number; isFuture: boolean; dayOfWeek: number }[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const djs = now.date(d);
    list.push({
      date: djs.format("YYYY-MM-DD"),
      count: heatmap[djs.format("YYYY-MM-DD")] ?? 0,
      isFuture: d > today,
      dayOfWeek: djs.day(),
    });
  }
  return list;
}

/** 把状态分布按分桶聚合 */
function groupStatusDistribution(dist: Record<string, number> | undefined) {
  if (!dist) return [] as { label: string; value: number; color: string }[];
  return STATUS_GROUPS.map(g => ({
    label: g.label,
    value: g.keys.reduce((sum, k) => sum + (dist[k] ?? 0), 0),
    color: g.color,
  })).filter(g => g.value > 0);
}

// ============ 子组件（提到组件外，避免每帧重建） ============
const EmptyChart: React.FC<{ height?: number; text?: string }> = ({ height = 140, text = "暂无数据" }) => (
  <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <Empty description={text} style={{ margin: 0 }} />
  </div>
);

const InfoTip: React.FC<{ content: string }> = ({ content }) => (
  <Tooltip content={content} position="top">
    <span style={infoIconStyle}><IconChevronRight style={{ fontSize: 14 }} /></span>
  </Tooltip>
);

const MetricItem: React.FC<{
  label: string; value: number | null | undefined; unit?: string;
  tip: string; compare?: React.ReactNode;
}> = ({ label, value, unit = "人", tip, compare }) => (
  <div style={metricItemStyle}>
    <span style={{ fontSize: T.fontSize.xs, color: T.color.textSub, fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}>
      {label}
      <InfoTip content={tip} />
    </span>
    <span style={{ fontSize: T.fontSize.lg, fontWeight: 700, color: T.color.text, lineHeight: 1.2 }}>
      {value ?? 0} <span style={{ fontSize: T.fontSize.xs, fontWeight: 400, color: T.color.textSub }}>{unit}</span>
    </span>
    {compare && <span style={{ fontSize: T.fontSize.xs, display: "flex", alignItems: "center", gap: 4 }}>{compare}</span>}
  </div>
);

const HeatmapCell: React.FC<{ bg: string; label: string; isFuture: boolean }> = ({ bg, label, isFuture }) => (
  <Tooltip content={label}>
    <div
      role="img"
      aria-label={label}
      title={label}
      className="up-cell"
      style={{
        width: 14, height: 14, borderRadius: 3, backgroundColor: bg,
        cursor: "pointer", opacity: isFuture ? 0.4 : 1,
        transition: "transform 0.15s ease",
      }}
    />
  </Tooltip>
);

// ============ 主组件 ============
export default function UserProfile() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromTeamPortrait = searchParams.get("from") === "teamPortrait";

  const [data, setData] = useState<PortraitData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>(
    () => searchParams.get("userId")
      || String(localStorage.getItem("userId") ?? ""),
  );
  const [period, setPeriod] = useState<string>(
    () => searchParams.get("period") || "today",
  );

  useEffect(() => {
    if (!selectedUserId) {
      setError("未获取到当前用户 ID，请重新登录");
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await apiClient.request<PortraitData>(
          `/sales-portrait/${selectedUserId}`, "GET", { period }
        );
        if (cancelled) return;
        if (resp.code === "ok") {
          setData(resp.data);
        } else {
          setError(resp.msg || "加载失败");
          Toast.error(resp.msg || "加载失败");
        }
      } catch (e: any) {
        if (!cancelled) {
          const msg = e?.message || "网络异常，请稍后重试";
          setError(msg);
          Toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [selectedUserId, period]);

  // 派生值统一 memo，避免每帧重算
  const groupedStatus = useMemo(
    () => groupStatusDistribution(data?.customerStructure?.statusDistribution),
    [data?.customerStructure?.statusDistribution],
  );
  const monthDays = useMemo(
    () => generateMonthDays(data?.followBehavior?.dailyHeatmap ?? {}),
    [data?.followBehavior?.dailyHeatmap],
  );
  const maxHeatCount = useMemo(
    () => Math.max(1, ...monthDays.filter(d => !d.isFuture).map(d => d.count)),
    [monthDays],
  );
  const totalCustomers = data?.customerStructure?.totalCustomers ?? 0;
  const totalBase = Math.max(totalCustomers, 1);

  // 加载态：首次进入无数据 → 全屏 Spin；后续刷新 → 角标遮罩
  if (loading && !data) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div style={{ padding: T.space[6], maxWidth: 1340, margin: "0 auto" }}>
        <Banner type="danger" description={error} />
        <div style={{ marginTop: T.space[4], textAlign: "center" }}>
          <Button onClick={() => setPeriod(p => p)} theme="solid" type="primary">重试</Button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { identity, coreMetrics, customerStructure, followBehavior, funnel } = data;
  const periodLabel = PERIOD_LABEL[period] ?? "今日";

  return (
    <div style={{
      padding: T.space[5], backgroundColor: T.color.bg, minHeight: "100vh",
      maxWidth: 1340, margin: "0 auto", fontFamily, position: "relative",
    }}>
      {/* 局部刷新遮罩（不隐藏已有数据，避免抖动） */}
      {loading && (
        <div style={{
          position: "absolute", top: T.space[5], right: T.space[5], zIndex: 10,
          background: "rgba(255,255,255,0.85)",
          padding: `${T.space[1]}px ${T.space[3]}px`,
          borderRadius: T.radius.sm, boxShadow: `0 2px 6px rgba(0,0,0,0.08)`,
          display: "flex", alignItems: "center", gap: T.space[2],
          fontSize: T.fontSize.xs, color: T.color.textSub,
        }}>
          <Spin /> 刷新中…
        </div>
      )}

      {fromTeamPortrait && (
        <Button
          theme="borderless"
          icon={<IconArrowLeft />}
          onClick={() => navigate("/teamPortrait")}
          style={{ marginBottom: T.space[3], paddingLeft: 0 }}
        >
          返回团队画像
        </Button>
      )}

      {/* ========== Identity Card ========== */}
      <Card style={cardStyle} className="up-card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: T.space[4] }}>
          <div style={{ display: "flex", alignItems: "center", gap: T.space[4] }}>
            <div style={{
              width: 64, height: 64, borderRadius: T.radius.md,
              background: `linear-gradient(135deg, ${T.color.primary}, ${T.color.primarySoft})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 700, fontSize: T.fontSize.xl,
            }}>
              {identity.userName?.slice(0, 2) || "?"}
            </div>
            <div>
              <h2 style={{ fontWeight: 700, fontSize: T.fontSize.lg, margin: 0, color: T.color.text, fontFamily }}>
                {identity.userName || "-"}
              </h2>
              <div style={{ display: "flex", gap: T.space[3], color: T.color.textSub, fontSize: T.fontSize.xs, marginTop: T.space[1] }}>
                <span><IconBranch style={{ fontSize: 12, marginRight: 4 }} />{identity.teamName || "无团队"}</span>
                <span>入职 {identity.entryDays ?? "-"} 天
                  <InfoTip content="从入职日期计算至今的在职天数，用于区分新老销售。" />
                </span>
              </div>
              <div style={{ marginTop: T.space[2], display: "flex", alignItems: "center", gap: T.space[2] }}>
                <Tag color={identity.online ? "green" : "grey"} shape="circle" size="small">
                  {identity.online ? "在线" : "离线"}
                </Tag>
                <span style={{ fontSize: T.fontSize.xs, color: T.color.textSub }}>
                  角色：{identity.roleName || "-"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div style={{ height: T.space[5] }} />

      {/* ========== Core Metrics ========== */}
      <Card style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: T.space[4] }}>
          <h3 style={sectionTitleStyle}>
            <IconHistogram style={{ color: T.color.primary }} /> 核心业绩指标
          </h3>
          <ButtonGroup size="small" theme="borderless">
            <Button onClick={() => setPeriod("today")} type={period === "today" ? "primary" : "tertiary"}>今日</Button>
            <Button onClick={() => setPeriod("week")} type={period === "week" ? "primary" : "tertiary"}>本周</Button>
            <Button onClick={() => setPeriod("month")} type={period === "month" ? "primary" : "tertiary"}>本月</Button>
          </ButtonGroup>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: T.space[3] }}>
          <MetricItem
            label={`${periodLabel}分配新客`} value={coreMetrics.todayAssignedNew}
            tip="当前时间段内系统分配给该销售的新客户总数，衡量获客能力。"
            compare={coreMetrics.todayAssignedNew > 0
              ? <Tag size="small" color="green" shape="circle">有新客</Tag> : null}
          />
          <MetricItem
            label="待跟进客户" value={coreMetrics.pendingCount}
            tip="当前需要跟进的客户总数，包括新分配未首跟或约定回访已到期的客户。"
            compare={coreMetrics.overdueCount > 0
              ? <Tag size="small" color="red" shape="circle">
                  <IconAlertTriangle style={{ fontSize: 12, marginRight: 2 }} />
                  {coreMetrics.overdueCount}人超时
                </Tag>
              : null}
          />
          <MetricItem
            label={`${periodLabel}跟进新客`} value={coreMetrics.todayFollowNew}
            tip="当前时间段内实际跟进的新客户人数，反映新客开发活跃度。"
            compare={coreMetrics.todayAssignedNew > 0
              ? <span style={{ color: T.color.textSub }}>进度 {Math.round((coreMetrics.todayFollowNew / coreMetrics.todayAssignedNew) * 100)}%</span>
              : null}
          />
          <MetricItem
            label={`${periodLabel}跟进老客`} value={coreMetrics.todayFollowOld}
            tip="当前时间段内跟进的已成交或维系中的老客户数量，评估老客维护力度。"
            compare={coreMetrics.todayFollowOld > 0
              ? <Tag size="small" color="green" shape="circle">有跟进</Tag> : null}
          />
          <MetricItem
            label={`${periodLabel}邀约上门`} value={coreMetrics.todayVisit}
            tip="当前时间段内实际到店的邀约客户数，是意向转化的关键节点。"
          />
          <MetricItem
            label="本月邀约上门" value={coreMetrics.monthVisit}
            tip="本月实际到店的邀约客户数，是意向转化的关键节点。"
            compare={coreMetrics.monthVisit > 0
              ? <Tag size="small" color="green" shape="circle">达成 {coreMetrics.monthVisit} 人</Tag> : null}
          />
        </div>

        <div style={{
          marginTop: T.space[3], padding: T.space[3],
          background: T.color.itemBg, borderRadius: T.radius.md,
          fontSize: T.fontSize.sm, color: T.color.text, display: "flex", gap: T.space[5], flexWrap: "wrap",
        }}>
          <span>本月累计新客 <strong>{coreMetrics.monthAssignedNew ?? 0}</strong> 人
            <InfoTip content="当月所有新分配的客户总数，反映月度获客规模。" />
          </span>
          <span>新客1小时跟进率 <strong>{pct(coreMetrics.oneHourFollowRate)}%</strong>
            <InfoTip content="分配后1小时内完成首次跟进的新客占比，目标通常≥90%，越高客户体验越好。" />
          </span>
        </div>
      </Card>

      <div style={{ height: T.space[5] }} />

      {/* ========== Customer Structure + Follow Behavior ========== */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: T.space[5] }}>
        {/* 客户结构 */}
        <Card style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: T.space[4] }}>
            <h3 style={sectionTitleStyle}><IconBranch style={{ color: T.color.primary }} /> 客户结构</h3>
            <span style={{ color: T.color.textSub, fontSize: T.fontSize.xs }}>共 {totalCustomers} 位客户</span>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: T.space[4] }}>
            {/* 星级分布 */}
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontWeight: 600, fontSize: T.fontSize.xs, marginBottom: T.space[2], color: T.color.text }}>
                <IconStar style={{ fontSize: 12, marginRight: 4, color: T.color.warning }} />按星级分布
                <InfoTip content="根据客户资质标签自动评定的星级比例，五星占比过低说明高质量客源不足。" />
              </div>
              {customerStructure.starDistribution && Object.keys(customerStructure.starDistribution).length > 0 ? (
                <VChart
                  spec={{
                    type: "pie",
                    data: [{
                      id: "star",
                      values: Object.entries(customerStructure.starDistribution).map(([k, v]) => ({
                        type: `${k}星`, value: v,
                      })),
                    }],
                    valueField: "value", categoryField: "type",
                    outerRadius: 0.85, innerRadius: 0.55, padAngle: 0.6,
                    color: STAR_COLORS,
                    label: {
                      visible: true, position: "outside",
                      formatter: "{value}",
                      style: { fontSize: 10 },
                    },
                    legends: { visible: false },
                  }}
                  options={{ mode: "desktop-browser" }}
                  style={{ height: 160 }}
                />
              ) : <EmptyChart />}
            </div>

            {/* 状态分布（分桶后） */}
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontWeight: 600, fontSize: T.fontSize.xs, marginBottom: T.space[2], color: T.color.text }}>
                <IconList style={{ fontSize: 12, marginRight: 4, color: T.color.primarySoft }} />按跟进状态
                <InfoTip content="当前客户所处的跟进阶段分布，用于判断转化健康度。" />
              </div>
              {groupedStatus.length > 0 ? (
                <VChart
                  spec={{
                    type: "pie",
                    data: [{
                      id: "status",
                      values: groupedStatus.map(g => ({
                        type: g.label, value: g.value,
                      })),
                    }],
                    valueField: "value", categoryField: "type",
                    outerRadius: 0.85, innerRadius: 0.55, padAngle: 0.6,
                    color: groupedStatus.map(g => g.color),
                    label: {
                      visible: true, position: "outside",
                      formatter: "{value}",
                      style: { fontSize: 10 },
                    },
                    legends: { visible: false },
                  }}
                  options={{ mode: "desktop-browser" }}
                  style={{ height: 160 }}
                />
              ) : <EmptyChart />}
            </div>
          </div>

          <div style={{ height: 1, background: T.color.border, margin: `${T.space[4]}px 0` }} />

          {/* 资质标签 */}
          <div style={{ fontSize: T.fontSize.xs, color: T.color.textSub, display: "flex", flexWrap: "wrap", gap: T.space[2], alignItems: "center" }}>
            <span>资质标签：</span>
            {customerStructure.tagDistribution && Object.entries(customerStructure.tagDistribution).filter(([, v]) => v > 0).map(([k, v]) => (
              <Tag key={k} size="small" color="blue" shape="circle">
                {TAG_LABELS[k] || k} {Math.round((v / totalBase) * 100)}%
              </Tag>
            ))}
            {(!customerStructure.tagDistribution || Object.values(customerStructure.tagDistribution).every(v => v === 0)) && (
              <span style={{ color: T.color.textMuted }}>暂无</span>
            )}
            <InfoTip content="客户具备的资产或信用标签的覆盖比例，帮助判断客群特征和产品匹配。" />
          </div>

          {/* 客户分类 KPI */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: T.space[2],
            marginTop: T.space[3], fontSize: T.fontSize.xs,
          }}>
            {[
              { label: "我的客户", value: customerStructure.newCount },
              { label: "重要客户", value: customerStructure.favCount },
              { label: "再分配客户", value: customerStructure.reassignedCount },
              { label: "本月流失", value: customerStructure.monthChurn, danger: true },
            ].map(item => (
              <div key={item.label} style={{
                textAlign: "center", padding: `${T.space[2]}px 0`,
                background: T.color.itemBg, borderRadius: T.radius.sm,
              }}>
                <div style={{ color: T.color.textSub }}>{item.label}</div>
                <div style={{ fontSize: T.fontSize.md, fontWeight: 700, color: item.danger ? T.color.danger : T.color.text }}>
                  {item.value ?? 0}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* 跟进行为分析 */}
        <Card style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: T.space[4] }}>
            <h3 style={sectionTitleStyle}><IconPhoneStroke style={{ color: T.color.primary }} /> 跟进行为分析</h3>
            <span style={{ color: T.color.textSub, fontSize: T.fontSize.xs }}>本月数据</span>
          </div>

          {/* 1 小时跟进率环图 */}
          <div style={{ display: "flex", alignItems: "center", gap: T.space[4], marginBottom: T.space[4] }}>
            <div style={{
              width: 96, height: 96, borderRadius: "50%", position: "relative", flexShrink: 0,
              background: `conic-gradient(${T.color.primary} 0deg ${3.6 * (followBehavior.oneHourFollowRate ?? 0)}deg, ${T.color.border} ${3.6 * (followBehavior.oneHourFollowRate ?? 0)}deg 360deg)`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <div style={{
                width: 72, height: 72, borderRadius: "50%", background: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <span style={{ fontWeight: 800, fontSize: T.fontSize.md, color: T.color.text }}>
                  {pct(followBehavior.oneHourFollowRate)}%
                </span>
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: T.fontSize.sm, color: T.color.text }}>
                新客1小时跟进率
                <InfoTip content="新客户分配后1小时内完成首次跟进的比例，行业优秀线通常为90%以上。" />
              </div>
              <div style={{ color: T.color.textSub, fontSize: T.fontSize.xs, marginTop: T.space[1] }}>
                目标 90% · {(followBehavior.oneHourFollowRate ?? 0) >= 90
                  ? <Tag size="small" color="green" shape="circle">超出 {(followBehavior.oneHourFollowRate ?? 0) - 90}%</Tag>
                  : <Tag size="small" color="red" shape="circle">差距 {90 - (followBehavior.oneHourFollowRate ?? 0)}%</Tag>}
              </div>
            </div>
          </div>

          {/* 跟进时段分布 */}
          <div style={{ fontWeight: 600, fontSize: T.fontSize.xs, color: T.color.text, margin: `${T.space[2]}px 0` }}>
            <IconCalendar style={{ fontSize: 12, marginRight: 4 }} />跟进时段分布（通话次数）
            <InfoTip content="不同时段的通话次数分布，可引导销售优化时间分配。" />
          </div>
          {followBehavior.hourlyDistribution && Object.keys(followBehavior.hourlyDistribution).length > 0 ? (
            <VChart
              spec={{
                type: "bar",
                data: [{
                  id: "hourly",
                  values: Object.entries(followBehavior.hourlyDistribution)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([k, v]) => ({ hour: `${k}时`, count: v })),
                }],
                xField: "hour", yField: "count",
                bar: {
                  style: {
                    fill: ({ datum }: any) => {
                      const m = Number((datum?.hour ?? "").toString().replace("时", ""));
                      if (m >= 19 && m < 21) return T.color.primary;
                      if (m >= 14 && m < 19) return T.color.primarySoft;
                      return "#8a9bc7";
                    },
                    radius: [4, 4, 0, 0],
                  },
                },
                axes: [
                  { orient: "bottom", label: { style: { fontSize: 10 } } },
                  { orient: "left", visible: false },
                ],
              }}
              options={{ mode: "desktop-browser" }}
              style={{ height: 100 }}
            />
          ) : <EmptyChart height={100} />}

          {/* 时段图例 */}
          <div style={{ display: "flex", gap: T.space[3], fontSize: T.fontSize.xs, color: T.color.textSub, marginTop: T.space[1] }}>
            <span><span style={{ display: "inline-block", width: 8, height: 8, background: T.color.primary, marginRight: 4 }} />黄金 19-21时</span>
            <span><span style={{ display: "inline-block", width: 8, height: 8, background: T.color.primarySoft, marginRight: 4 }} />一般 14-19时</span>
            <span><span style={{ display: "inline-block", width: 8, height: 8, background: "#8a9bc7", marginRight: 4 }} />其他</span>
          </div>

          {/* 签约 vs 流失 对比条 */}
          <div style={{ marginTop: T.space[3], fontSize: T.fontSize.xs }}>
            <div style={{ display: "flex", alignItems: "center", gap: T.space[2], marginBottom: T.space[1] }}>
              <IconTick style={{ color: T.color.success, fontSize: T.fontSize.sm }} />
              <span style={{ flex: 1, color: T.color.textSub }}>签约客户平均跟进</span>
              <strong style={{ color: T.color.success }}>{pct(followBehavior.avgSignedFollowCount, 1)} 次</strong>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
              <IconChevronDown style={{ color: T.color.danger, fontSize: T.fontSize.sm }} />
              <span style={{ flex: 1, color: T.color.textSub }}>流失客户平均跟进</span>
              <strong style={{ color: T.color.danger }}>{pct(followBehavior.avgChurnFollowCount, 1)} 次</strong>
            </div>
          </div>

          {followBehavior.weakFollowNewCount > 0 && (
            <div style={{
              marginTop: T.space[2], padding: T.space[2],
              background: T.color.warningBg, borderRadius: T.radius.sm,
              color: T.color.warning, fontSize: T.fontSize.xs, display: "flex", alignItems: "center", gap: T.space[1],
            }}>
              <IconAlertTriangle style={{ fontSize: 12 }} />
              {followBehavior.weakFollowNewCount} 个新客分配满7天、累计跟进＜2次，存在流失风险
              <InfoTip content="分配已满7天但累计跟进仍不足2次的未成交客户数量，提示孵化不足。" />
            </div>
          )}
        </Card>
      </div>

      <div style={{ height: T.space[5] }} />

      {/* ========== Funnel + Heatmap ========== */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: T.space[5] }}>
        {/* 漏斗 */}
        <Card style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: T.space[4] }}>
            <h3 style={sectionTitleStyle}>
              <IconChevronRight style={{ color: T.color.primary }} /> 本月客户转化漏斗
              <InfoTip content="展示从分配新客到最终放款各环节的人数。" />
            </h3>
          </div>
          {funnel.totalAssigned > 0 ? (
            <>
              {FUNNEL_STEPS.map((step, i) => {
                const value = (funnel as any)[step.key] as number;
                const maxVal = funnel.totalAssigned || 1;
                const widthPct = Math.max((value / maxVal) * 100, 3);
                const prevValue = i > 0 ? (funnel as any)[FUNNEL_STEPS[i - 1].key] as number : value;
                const convRate = i > 0 && prevValue > 0 ? ((value / prevValue) * 100).toFixed(1) : null;
                const barColors = [T.color.primarySoft, T.color.primarySoft, "#5c72a8", "#3a5290", T.color.primary];
                return (
                  <div key={step.key} style={{ display: "flex", alignItems: "center", gap: T.space[2], margin: `${T.space[2]}px 0` }}>
                    <span style={{ width: 80, fontSize: T.fontSize.xs, color: T.color.textSub }}>{step.label}</span>
                    <div style={{ flex: 1, height: 26, background: T.color.itemBg, borderRadius: T.radius.md, overflow: "hidden" }}>
                      <div style={{
                        width: `${widthPct}%`, height: "100%", borderRadius: T.radius.md,
                        background: barColors[i], color: "#fff", fontSize: T.fontSize.xs,
                        paddingLeft: T.space[2], lineHeight: "26px", fontWeight: 500,
                        whiteSpace: "nowrap", transition: "width 0.5s ease",
                      }}>
                        {value}
                      </div>
                    </div>
                    {convRate !== null ? (
                      <span style={{ fontSize: T.fontSize.xs, color: T.color.textSub, width: 50, textAlign: "right" }}>
                        {convRate}%
                      </span>
                    ) : <span style={{ width: 50 }} />}
                  </div>
                );
              })}
              <div style={{ color: T.color.textSub, fontSize: T.fontSize.xs, marginTop: T.space[2] }}>
                意向→邀约转化率 <strong>{pct(funnel.intentToVisitRate, 1)}%</strong>
                <InfoTip content="从产生意向到实际邀约上门的转化比率。" />
              </div>
            </>
          ) : <Empty description="本月暂无分配数据" />}
        </Card>

        {/* 热力图（GitHub 风格：7 行按周排列） */}
        <Card style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: T.space[4] }}>
            <h3 style={sectionTitleStyle}>
              <IconCalendar style={{ color: T.color.primary }} /> 本月跟进热力图
              <InfoTip content="每日跟进次数以颜色深浅表示，深色为跟进较多，浅色为较少。" />
            </h3>
            <span style={{ color: T.color.textSub, fontSize: T.fontSize.xs }}>日均跟进 {pct(followBehavior.dailyAvgFollowCount)} 次</span>
          </div>

          <div style={{
            display: "grid",
            gridTemplateRows: "repeat(7, 14px)",
            gridAutoFlow: "column",
            gap: 3,
            justifyContent: "start",
          }}>
            {monthDays.map(({ date, count, isFuture }) => {
              const intensity = count / maxHeatCount;
              const bg = isFuture ? "#f8fafc"
                : count === 0 ? "#e2e7f0"
                : intensity < 0.25 ? "#cbd5e1"
                : intensity < 0.5 ? "#94a3b8"
                : intensity < 0.75 ? "#64748b"
                : "#334155";
              const label = isFuture ? `${date}：未完成` : count === 0 ? `${date}：休息` : `${date}：${count} 次`;
              return <HeatmapCell key={date} bg={bg} label={label} isFuture={isFuture} />;
            })}
          </div>

          {/* 图例（色块与实际渲染色一致） */}
          <div style={{ display: "flex", gap: T.space[3], fontSize: T.fontSize.xs, color: T.color.textSub, marginTop: T.space[2], alignItems: "center" }}>
            <span>少</span>
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#e2e7f0" }} />
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#cbd5e1" }} />
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#94a3b8" }} />
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#64748b" }} />
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#334155" }} />
            <span>多</span>
          </div>
        </Card>
      </div>

      {/* CSS-in-JS：hover 效果（替代 onMouseEnter 直接操作 DOM） */}
      <style>{`
        .up-cell:hover { transform: scale(1.3); }
        .up-card:hover { box-shadow: 0 12px 24px rgba(0,0,0,0.06), 0 4px 8px rgba(0,20,40,0.06); }
      `}</style>
    </div>
  );
}
